import React from 'react';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import includes from 'lodash/includes';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import setDisplayName from 'recompose/setDisplayName';
import ConfirmCancelModal from 'components/common/ConfirmCancelModal';
import ImageEditModal from "components/images/ImageEditModal";
import {fromSources} from 'observables/lettables/pickFile';
import {TempImageType} from "utils/DBUtils";
import {fillImageDataFromFile} from "./utils";
import {
    openConfirmCancelModalAction,
    closeConfirmCancelModalAction,
    endUploadOriginalAction,
    uploadedOriginalAction,
    uploadOriginalAction,
    saveImageDataAction
} from "./actions";

/**
 * This is a specialized container for ImageEditModal to handle editing the initial data when uploading an original image
 * Since the image is not a saved image record in the API yet, here the data is mocked and passed to the image edit modal
 */

const mapStateToProps = (state) => {
    let selectedImage;
    let hasBeenUploaded = false;

    if (state.images.selectedPendingImage) {
        selectedImage = state.images.selectedPendingImage;
        hasBeenUploaded = state.images.selectedPendingImage.uploaded === true;
    }

    const currentEditBreakId = state.editBreak.break ? state.editBreak.break[state.editBreak.currentPlatform] : null;
    const currentEditBreak = currentEditBreakId ? state.resources.resources[currentEditBreakId] : null;

    if (currentEditBreak && selectedImage) {
        selectedImage.sponsoredType = currentEditBreak.sponsoredType;
    }

    const siblingImages = state.images.selectedImageSiblings.map(id => state.resources.resources[id]);

    return {
        hasBeenUploaded,
        uploadingImagesFor: state.images.uploadingImagesFor,
        data: selectedImage,
        isOpen: state.images.editingImage === false && state.images.pendingImages.length > 0,
        didConfirmCancelation: state.images.didConfirmCancelBulkUpload,
        pendingImages: state.images.pendingImages,
        selectedPendingImage: state.images.selectedPendingImage,
        selectedPendingImageIndex: state.images.selectedPendingImageIndex,
        selectedImageSiblings: siblingImages,
        editingImage: state.images.savingImageData,
        uploadingImage: state.images.uploadingImage
    };
};

const mapDispatchToProps = dispatch => bindActionCreators({
    finishEditing: uploadedOriginalAction,
    onClose: endUploadOriginalAction,
    closeConfirmCancelModal: closeConfirmCancelModalAction,
    openConfirmCancelModal: openConfirmCancelModalAction,
    uploadOriginal: uploadOriginalAction,
    saveImageData: saveImageDataAction
}, dispatch);

const ConfirmCancelModalContainer = compose(
  setDisplayName('ConfirmCancelModalContainer'),
  connect(state => ({
    isOpen: state.images.isConfirmCancelModalOpen,
    headerKey: 'modal.confirmCancel.bulkUpload.header',
    messageKey: 'modal.confirmCancel.bulkUpload.message'
  }))
)(ConfirmCancelModal);

export default compose(
    setDisplayName('UploadOriginalEditImageModalContainer'),
    connect(mapStateToProps, mapDispatchToProps),
    withProps(props => {

        // when uploading in bulk, the submit action changes dynamically depending on where
        // in the workflow the user is:
        //   - initially, each submit button uploads the current image.
        //   - when an image has been uploaded, its submit button saves edits to its info.
        //   - when all images have been uploaded, the submit button changes to say
        //     "Done", and when clicked saves the current image's info, closes the modal,
        //     and fetches the newly uploaded images.
        //
        // (see DBRKCP-9487)

        const saveImageResource = data => {
            // ensure that we're saving to the correct resource id.
            data.id = props.selectedPendingImage.id;
            props.saveImageData(data);
        };

        // saves the current selected image in case there are still any pending edits, then closes the modal.
        const saveThenFinish = data => {
            saveImageResource(data);

            // close the modal and provide break resource id to fetch new images.
            props.finishEditing({createdFor: props.uploadingImagesFor});
        };

        // uploads if the image hasn't been already, otherwise just saves whatever edits were made to image data.
        const uploadOrSave = data => {
            if (props.hasBeenUploaded) {
                saveImageResource(data);
            } else {
                props.uploadOriginal(data);
            }
        };

        const allImagesWereUploaded = props.pendingImages.every(image => image.uploaded === true);

        const submitButtonText = allImagesWereUploaded ? 'Done' : 'Save';
        const onSaveData = allImagesWereUploaded ? saveThenFinish : uploadOrSave;

        return {
            submitButtonText,
            onSaveData,

            // attempting to close this modal will open the confirm cancelation modal.
            onClose: props.openConfirmCancelModal,

            confirmCancelModalProps: {

                // the confirm cancelation modal can itself be closed like any modal.
                onClose: props.closeConfirmCancelModal,

                onSubmit: () => {
                    // complete the process, discarding unsaved work since the user has by
                    // now confirmed that that would be totally chill.
                    props.finishEditing({createdFor: props.uploadingImagesFor});
                    props.closeConfirmCancelModal();
                }
            }
        };
    })
)(props => {
    const {confirmCancelModalProps, ...rest} = props;

    return (
        <React.Fragment>
          <ImageEditModal {...rest} />
          <ConfirmCancelModalContainer {...confirmCancelModalProps} />
        </React.Fragment>
    );
});



// WEBPACK FOOTER //
// ./public/pages/Images/UploadOriginalEditImageModalContainer.jsx